/**
 * @file Enhanced drag-and-drop import zone component with collection selection and sequential import
 * @version 2.0.0
 * @author Claude Assistant
 * @since 2025-07-09
 */

import { useDataPortability } from '../composables/dataPortability.js';
import { useNotifications } from '../../core/errors.js';
import { useDb } from '../../core/db.js';
import { getCollection } from '../../collections/composables/collection.js';
import FileDropzone from './FileDropzone.js';
import CollectionSelector from '../../collections/components/CollectionSelector.js';
import SingleCollectionImport from './SingleCollectionImport.js';

const { ref, computed } = Vue;

export default {
  name: 'DataImportZone',

  components: {
    FileDropzone,
    CollectionSelector,
    SingleCollectionImport,
  },

  template: `
    <div class="data-import-zone">
      <!-- File Drop Zone (shown when no file selected) -->
      <div v-if="!selectedFile">
        <FileDropzone 
          title="Drop JSON file or click to browse"
          subtitle="Import individual collection or full database"
          :accepted-formats="acceptedFormats"
          :max-file-size="maxFileSize"
          @file-selected="handleFileSelection"
          @file-error="handleFileError"
        />
      </div>
      
      <!-- File Preview and Import Options -->
      <div v-if="selectedFile && !isImportStarted" class="mt-3">
        <div class="card">
          <div class="card-body">
            <h6 class="card-title">
              <i class="bi-file-earmark-text me-2"></i>
              {{ selectedFile.name }}
            </h6>
            <div class="row">
              <div class="col-md-6">
                <small class="text-muted">Size: {{ formatFileSize(selectedFile.size) }}</small>
              </div>
              <div class="col-md-6">
                <small class="text-muted">Modified: {{ formatDate(selectedFile.lastModified) }}</small>
              </div>
            </div>
            
            <!-- Import preview -->
            <div v-if="importPreview" class="mt-3">
              <!-- Single Collection Import -->
              <div v-if="isSingleCollection">
                <div class="alert alert-info">
                  <strong>{{ importPreview.collection }}</strong> - {{ importPreview.recordCount }} records
                </div>

                <div class="d-flex gap-2">
                  <button class="btn btn-primary" @click="startSingleCollectionImport">
                    <i class="bi-play me-2"></i>
                    Start Import
                  </button>
                  <button class="btn btn-secondary" @click="resetComponent">
                    <i class="bi-x-circle me-2"></i>
                    Cancel
                  </button>
                </div>
              </div>

              <!-- Config Import -->
              <div v-else-if="isConfig">
                <div class="alert alert-info">
                  <strong>Config: {{ importPreview.config }}</strong>
                </div>

                <div class="d-flex gap-2">
                  <button class="btn btn-primary" @click="startConfigImport">
                    <i class="bi-play me-2"></i>
                    Import Config
                  </button>
                  <button class="btn btn-secondary" @click="resetComponent">
                    <i class="bi-x-circle me-2"></i>
                    Cancel
                  </button>
                </div>
              </div>
              
              <!-- Database Import Configuration -->
              <div v-else-if="isFullDatabase">
                <div class="alert alert-info">
                  <strong>Full Database Import</strong> - {{ importPreview.totalRecords }} records across {{ importPreview.collectionCount }} collections<span v-if="importPreview.configCount">, {{ importPreview.configCount }} configs</span>
                </div>
                
                <!-- Collection Selection -->
                <div class="mb-3">
                  <h6>Select Collections to Import</h6>
                  <CollectionSelector
                    :collections="availableCollections"
                    v-model="selectedCollections"
                  />
                </div>

                <!-- Config Selection -->
                <div v-if="availableConfigs.length > 0" class="mb-3">
                  <h6>Select Configs to Import</h6>
                  <div v-for="config in availableConfigs" :key="config" class="form-check">
                    <input
                      :id="'import-config-' + config"
                      class="form-check-input"
                      type="checkbox"
                      :value="config"
                      v-model="selectedConfigs"
                    >
                    <label class="form-check-label" :for="'import-config-' + config">
                      {{ config }}
                    </label>
                  </div>
                </div>

                <!-- Import Strategy Selection -->
                <div class="mb-3">
                  <label class="form-label">Import Strategy</label>
                  <select class="form-select" v-model="importStrategy">
                    <option value="collection-by-collection">Collection by Collection (sequential with individual control)</option>
                    <option value="direct-upsert">Direct Import - Upsert (update existing, add new)</option>
                    <option value="direct-replace">Direct Import - Replace (clear collections first)</option>
                  </select>
                </div>

                <!-- Import Actions -->
                <div class="d-flex gap-2">
                  <button
                    class="btn btn-primary"
                    @click="startImport"
                    :disabled="selectedCollections.length === 0 && selectedConfigs.length === 0"
                  >
                    <i class="bi-play me-2"></i>
                    Start Import ({{ selectedCollections.length }} collections, {{ selectedConfigs.length }} configs)
                  </button>
                  <button class="btn btn-secondary" @click="resetComponent">
                    <i class="bi-x-circle me-2"></i>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Import Progress (shown during import) -->
      <div v-if="isImportStarted && !isImportComplete" class="mt-3">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6>Import in Progress</h6>
              <button 
                class="btn btn-sm btn-outline-danger" 
                @click="stopImport"
                v-if="importStrategy === 'collection-by-collection'"
              >
                <i class="bi-stop me-1"></i>
                Stop Import
              </button>
            </div>
            
            <!-- Single Collection Progress -->
            <div v-if="isSingleCollection">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span>Importing: <strong>{{ importPreview.collection }}</strong></span>
                <small class="text-muted">{{ importPreview.recordCount }} records</small>
              </div>
              
              <SingleCollectionImport 
                :collection-name="importPreview.collection"
                :import-data="importPreview.data"
                @import-complete="handleSingleCollectionImportComplete"
              />
            </div>
            
            <!-- Collection-by-Collection Progress -->
            <div v-else-if="importStrategy === 'collection-by-collection' && currentCollection">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span>Importing: <strong>{{ currentCollection }}</strong></span>
                <small class="text-muted">{{ currentImportIndex + 1 }} of {{ selectedCollections.length }}</small>
              </div>
              
              <div class="progress mb-3">
                <div 
                  class="progress-bar" 
                  :style="{ width: importProgress + '%' }"
                  role="progressbar"
                ></div>
              </div>
              
              <SingleCollectionImport 
                v-if="currentImportData"
                :collection-name="currentCollection"
                :import-data="currentImportData"
                :show-skip-button="true"
                @import-complete="handleSequentialImportComplete"
                @import-skip="skipCurrentImport"
              />
            </div>
            
            <!-- Direct Import Progress -->
            <div v-else-if="importStrategy.startsWith('direct-')">
              <div class="progress mb-3">
                <div 
                  class="progress-bar progress-bar-striped progress-bar-animated" 
                  :style="{ width: importProgress + '%' }"
                  role="progressbar"
                ></div>
              </div>
              <div class="text-center">
                <span class="text-muted">Importing {{ selectedCollections.length }} collections...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Import Results -->
      <div v-if="isImportComplete" class="mt-3">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6>Import Results</h6>
              <button class="btn btn-primary" @click="resetComponent">
                <i class="bi-arrow-clockwise me-2"></i>
                Import Another File
              </button>
            </div>
            
            <div class="table-responsive">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>Collection</th>
                    <th>Status</th>
                    <th>Imported</th>
                    <th>Failed</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="result in importResults" :key="result.collection">
                    <td>{{ result.collection }}</td>
                    <td>
                      <span v-if="result.status === 'completed'" class="badge bg-success">Completed</span>
                      <span v-else-if="result.status === 'skipped'" class="badge bg-warning">Skipped</span>
                      <span v-else-if="result.status === 'failed'" class="badge bg-danger">Failed</span>
                      <span v-else class="badge bg-secondary">Pending</span>
                    </td>
                    <td>{{ result.imported || 0 }}</td>
                    <td>{{ result.failed || 0 }}</td>
                    <td>{{ result.total || 0 }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Summary -->
            <div class="mt-3">
              <div class="row text-center">
                <div class="col-md-3">
                  <div class="text-success">
                    <i class="bi-check-circle me-1"></i>
                    <strong>{{ completedCount }}</strong> Completed
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="text-warning">
                    <i class="bi-exclamation-triangle me-1"></i>
                    <strong>{{ skippedCount }}</strong> Skipped
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="text-danger">
                    <i class="bi-x-circle me-1"></i>
                    <strong>{{ failedCount }}</strong> Failed
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="text-primary">
                    <i class="bi-collection me-1"></i>
                    <strong>{{ totalRecordsImported }}</strong> Records
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,

  props: {
    collectionName: {
      type: String,
      required: false,
    },
    importableCollections: {
      type: Array,
      default: () => [],
    },
    acceptedFormats: {
      type: Array,
      default: () => ['json'],
    },
    maxFileSize: {
      type: Number,
      default: 10 * 1024 * 1024, // 10MB
    },
  },

  setup(props) {
    const {
      importCollection,
      importConfig,
      detectFileType,
      processImportFile,
      formatFileSize,
    } = useDataPortability();
    const { success, error, warning } = useNotifications();
    const db = useDb();

    const selectedFile = ref(null);
    const importPreview = ref(null);
    const selectedCollections = ref([]);
    const selectedConfigs = ref([]);
    const importStrategy = ref('collection-by-collection');
    const isImportStarted = ref(false);
    const isImportComplete = ref(false);
    const currentImportIndex = ref(0);
    const currentDirectImportIndex = ref(0);
    const importResults = ref([]);
    const importStopped = ref(false);

    const isSingleCollection = computed(() => {
      return importPreview.value && importPreview.value.type === 'collection';
    });

    const isConfig = computed(() => {
      return importPreview.value && importPreview.value.type === 'config';
    });

    const isFullDatabase = computed(() => {
      return importPreview.value && importPreview.value.type === 'database';
    });

    const availableCollections = computed(() => {
      if (isFullDatabase.value) {
        return Object.keys(importPreview.value.data.collections || {}).map(
          name => {
            const collectionData = importPreview.value.data.collections[name];
            const collection = getCollection(name);
            return {
              name,
              items: { length: collectionData.data.length },
              exportedAt: collectionData.exportMetadata?.exportedAt,
              replicationState: collection.replicationState,
            };
          }
        );
      }
      return [];
    });

    const availableConfigs = computed(() => {
      if (isFullDatabase.value) {
        return Object.keys(importPreview.value.data.configs || {}).filter(
          name => name !== '_user'
        );
      }
      return [];
    });

    const currentCollection = computed(() => {
      return selectedCollections.value[currentImportIndex.value] || '';
    });

    const currentImportData = computed(() => {
      if (!currentCollection.value) return null;
      return importPreview.value.data.collections[currentCollection.value];
    });

    const importProgress = computed(() => {
      if (selectedCollections.value.length === 0) return 0;
      if (importStrategy.value === 'collection-by-collection') {
        return Math.round(
          (currentImportIndex.value / selectedCollections.value.length) * 100
        );
      } else {
        return Math.round(
          (currentDirectImportIndex.value / selectedCollections.value.length) *
            100
        );
      }
    });

    const completedCount = computed(() => {
      return importResults.value.filter(r => r.status === 'completed').length;
    });

    const skippedCount = computed(() => {
      return importResults.value.filter(r => r.status === 'skipped').length;
    });

    const failedCount = computed(() => {
      return importResults.value.filter(r => r.status === 'failed').length;
    });

    const totalRecordsImported = computed(() => {
      return importResults.value.reduce((sum, r) => sum + (r.imported || 0), 0);
    });

    const handleFileSelection = async file => {
      selectedFile.value = file;
      importPreview.value = null;
      selectedCollections.value = [];
      selectedConfigs.value = [];
      resetImportState();

      try {
        const jsonData = await processImportFile(file);
        const fileType = detectFileType(jsonData);

        if (fileType === 'collection') {
          if (
            props.collectionName &&
            jsonData.collection !== props.collectionName
          ) {
            throw new Error(
              `Collection mismatch: expected ${props.collectionName}, got ${jsonData.collection}`
            );
          }
          importPreview.value = {
            type: 'collection',
            collection: jsonData.collection,
            recordCount: jsonData.data.length,
            exportedAt: jsonData.exportMetadata?.exportedAt,
            data: jsonData,
          };
        } else if (fileType === 'config') {
          importPreview.value = {
            type: 'config',
            config: jsonData.config,
            exportedAt: jsonData.exportMetadata?.exportedAt,
            data: jsonData,
          };
        } else if (fileType === 'database') {
          const totalRecords = Object.values(jsonData.collections || {}).reduce(
            (sum, col) => sum + (col.data?.length || 0),
            0
          );
          const configCount = Object.keys(jsonData.configs || {}).length;

          importPreview.value = {
            type: 'database',
            database: jsonData.database,
            collectionCount: Object.keys(jsonData.collections || {}).length,
            configCount,
            totalRecords,
            exportedAt: jsonData.exportMetadata?.exportedAt,
            data: jsonData,
          };

          // Auto-select all collections and configs by default
          if (props.importableCollections.length > 0) {
            selectedCollections.value = Object.keys(
              jsonData.collections || {}
            ).filter(name => props.importableCollections.includes(name));
          } else {
            selectedCollections.value = Object.keys(jsonData.collections || {});
          }
          selectedConfigs.value = Object.keys(jsonData.configs || {}).filter(
            name => name !== '_user'
          );
        }
      } catch (err) {
        error(`File error: ${err.message}`);
        resetComponent();
      }
    };

    const handleFileError = errorMessage => {
      error(errorMessage);
    };

    const startSingleCollectionImport = () => {
      isImportStarted.value = true;
      isImportComplete.value = false;
      importStopped.value = false;

      // Initialize results for single collection
      importResults.value = [
        {
          collection: importPreview.value.collection,
          status: 'pending',
          imported: 0,
          failed: 0,
          total: 0,
        },
      ];
    };

    const startConfigImport = async () => {
      isImportStarted.value = true;
      isImportComplete.value = false;

      try {
        await importConfig(
          importPreview.value.config,
          importPreview.value.data
        );
        importResults.value = [
          {
            collection: importPreview.value.config,
            status: 'completed',
            imported: 1,
            failed: 0,
            total: 1,
          },
        ];
        success(`Config "${importPreview.value.config}" imported successfully`);
      } catch (err) {
        importResults.value = [
          {
            collection: importPreview.value.config,
            status: 'failed',
            imported: 0,
            failed: 1,
            total: 1,
            error: err.message,
          },
        ];
        error(`Config import failed: ${err.message}`);
      }
      isImportComplete.value = true;
    };

    const startImport = async () => {
      isImportStarted.value = true;
      isImportComplete.value = false;
      importStopped.value = false;

      importResults.value = selectedCollections.value.map(name => ({
        collection: name,
        status: 'pending',
        imported: 0,
        failed: 0,
        total: 0,
      }));

      if (importStrategy.value === 'collection-by-collection') {
        currentImportIndex.value = 0;
      } else {
        await handleDirectImport();
      }
    };

    const handleDirectImport = async () => {
      const strategy = importStrategy.value.replace('direct-', '');
      currentDirectImportIndex.value = 0;

      try {
        // Import collections
        for (let i = 0; i < selectedCollections.value.length; i++) {
          if (importStopped.value) break;

          const collectionName = selectedCollections.value[i];
          const collectionData =
            importPreview.value.data.collections[collectionName];

          try {
            const result = await importCollection(
              collectionName,
              collectionData,
              { strategy }
            );
            importResults.value[i] = {
              collection: collectionName,
              status: 'completed',
              imported: result.summary.successful,
              failed: result.summary.failed,
              total: result.summary.total,
            };
          } catch (err) {
            importResults.value[i] = {
              collection: collectionName,
              status: 'failed',
              imported: 0,
              failed: 0,
              total: 0,
              error: err.message,
            };
          }
          currentDirectImportIndex.value = i + 1;
        }

        // Import selected configs
        const configs = importPreview.value.data.configs || {};
        for (const configName of selectedConfigs.value) {
          if (importStopped.value) break;
          try {
            await importConfig(configName, configs[configName]);
            importResults.value.push({
              collection: `config: ${configName}`,
              status: 'completed',
              imported: 1,
              failed: 0,
              total: 1,
            });
          } catch (err) {
            importResults.value.push({
              collection: `config: ${configName}`,
              status: 'failed',
              imported: 0,
              failed: 1,
              total: 1,
              error: err.message,
            });
          }
        }

        isImportComplete.value = true;
        success(
          `Direct import completed: ${completedCount.value} successful, ${failedCount.value} failed`
        );
      } catch (err) {
        error(`Import failed: ${err.message}`);
        isImportComplete.value = true;
      }
    };

    const handleSequentialImportComplete = async result => {
      importResults.value[currentImportIndex.value] = {
        collection: currentCollection.value,
        status: result.success !== false ? 'completed' : 'failed',
        imported: result.summary?.successful || 0,
        failed: result.summary?.failed || 0,
        total: result.summary?.total || 0,
      };

      currentImportIndex.value++;

      if (
        currentImportIndex.value >= selectedCollections.value.length ||
        importStopped.value
      ) {
        // Import selected configs after all collections are done
        const configs = importPreview.value.data.configs || {};
        for (const configName of selectedConfigs.value) {
          try {
            await importConfig(configName, configs[configName]);
            importResults.value.push({
              collection: `config: ${configName}`,
              status: 'completed',
              imported: 1,
              failed: 0,
              total: 1,
            });
          } catch (err) {
            importResults.value.push({
              collection: `config: ${configName}`,
              status: 'failed',
              imported: 0,
              failed: 1,
              total: 1,
              error: err.message,
            });
          }
        }

        isImportComplete.value = true;
        success(
          `Sequential import completed: ${completedCount.value} successful, ${failedCount.value} failed, ${skippedCount.value} skipped`
        );
      }
    };

    const skipCurrentImport = async () => {
      importResults.value[currentImportIndex.value] = {
        collection: currentCollection.value,
        status: 'skipped',
        imported: 0,
        failed: 0,
        total: 0,
      };

      currentImportIndex.value++;

      if (currentImportIndex.value >= selectedCollections.value.length) {
        // Import selected configs after all collections are done
        const configs = importPreview.value.data.configs || {};
        for (const configName of selectedConfigs.value) {
          try {
            await importConfig(configName, configs[configName]);
            importResults.value.push({
              collection: `config: ${configName}`,
              status: 'completed',
              imported: 1,
              failed: 0,
              total: 1,
            });
          } catch (err) {
            importResults.value.push({
              collection: `config: ${configName}`,
              status: 'failed',
              imported: 0,
              failed: 1,
              total: 1,
              error: err.message,
            });
          }
        }
        isImportComplete.value = true;
      }
    };

    const stopImport = () => {
      importStopped.value = true;
      isImportComplete.value = true;
    };

    const handleSingleCollectionImportComplete = result => {
      // Convert single collection result to the same format as database import results
      importResults.value = [
        {
          collection: importPreview.value.collection,
          status: result.success !== false ? 'completed' : 'failed',
          imported: result.summary?.successful || 0,
          failed: result.summary?.failed || 0,
          total: result.summary?.total || 0,
        },
      ];

      isImportComplete.value = true;
      if (result.success !== false) {
        success(`Import completed successfully`);
      }
    };

    const resetImportState = () => {
      isImportStarted.value = false;
      isImportComplete.value = false;
      currentImportIndex.value = 0;
      currentDirectImportIndex.value = 0;
      importResults.value = [];
      importStopped.value = false;
    };

    const resetComponent = () => {
      selectedFile.value = null;
      importPreview.value = null;
      selectedCollections.value = [];
      selectedConfigs.value = [];
      importStrategy.value = 'collection-by-collection';
      resetImportState();
    };

    const formatDate = timestamp => {
      return new Date(timestamp).toLocaleDateString();
    };

    return {
      selectedFile,
      importPreview,
      selectedCollections,
      selectedConfigs,
      importStrategy,
      isImportStarted,
      isImportComplete,
      currentImportIndex,
      importResults,
      isSingleCollection,
      isConfig,
      isFullDatabase,
      availableCollections,
      availableConfigs,
      currentCollection,
      currentImportData,
      importProgress,
      completedCount,
      skippedCount,
      failedCount,
      totalRecordsImported,
      handleFileSelection,
      handleFileError,
      startSingleCollectionImport,
      startConfigImport,
      startImport,
      handleSequentialImportComplete,
      skipCurrentImport,
      stopImport,
      handleSingleCollectionImportComplete,
      resetComponent,
      formatFileSize,
      formatDate,
    };
  },
};
